import { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, logout as apiLogout } from '../lib/api';

const AuthContext = createContext(null);

// Demo credentials for testing when no employees exist
const DEMO_CREDENTIALS = {
  email: 'admin@lunipin.com',
  password: 'Admin123!',
  user: {
    id: 'demo-admin-001',
    employee_id: 'DEMO001',
    name: 'Demo Admin',
    email: 'admin@lunipin.com',
    role: 'super_admin',
    requires_password_change: false,
  },
  token: 'demo-jwt-token-lunipin-admin-2024',
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('admin_user');
    const token = localStorage.getItem('admin_token');
    if (storedUser && token) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setRequiresPasswordChange(userData.requires_password_change || false);
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      // Try real API first (employee login with is_admin=true)
      const response = await apiLogin(email, password);
      const { token, user: userData } = response.data;
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_user', JSON.stringify(userData));
      setUser(userData);
      setRequiresPasswordChange(userData.requires_password_change || false);
      return userData;
    } catch (error) {
      // Fallback to demo login if API fails or credentials are demo credentials
      if (email === DEMO_CREDENTIALS.email && password === DEMO_CREDENTIALS.password) {
        localStorage.setItem('admin_token', DEMO_CREDENTIALS.token);
        localStorage.setItem('admin_user', JSON.stringify(DEMO_CREDENTIALS.user));
        setUser(DEMO_CREDENTIALS.user);
        setRequiresPasswordChange(false);
        return DEMO_CREDENTIALS.user;
      }
      // Re-throw error if demo credentials don't match
      throw error;
    }
  };

  const logout = () => {
    apiLogout();
    setUser(null);
    setRequiresPasswordChange(false);
  };

  const clearPasswordChangeFlag = () => {
    setRequiresPasswordChange(false);
    if (user) {
      const updatedUser = { ...user, requires_password_change: false };
      localStorage.setItem('admin_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      loading, 
      requiresPasswordChange,
      clearPasswordChangeFlag 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
